/**
 * Pure decisions behind client-side HLS frame capture: how big a captured frame
 * should be, what identifies it in the cache, which pending job to run next,
 * and how the cache is bounded.
 *
 * Deliberately free of DOM, `hls.js` and MobX imports so it stays unit-testable
 * under plain `npx tsx` — `hlsFrameCapture.ts` next door owns everything that
 * needs a browser. Keep it that way: importing a store here would drag
 * `localStorage` into the test runner.
 */
import { LRUCache } from 'lru-cache';

/**
 * Hard ceiling on a captured frame's longest side, regardless of what the
 * caller asked for. Large canvases are the expensive part of a grab and
 * Safari/iOS hold onto them, so nobody gets to opt out.
 */
export const MAX_CAPTURE_DIMENSION = 1280;

/** JPEG quality used when the caller doesn't state one. */
export const DEFAULT_CAPTURE_QUALITY = 0.72;

/**
 * How finely timestamps are bucketed before they become cache keys. Requests
 * landing in the same bucket share one capture.
 *
 * The default suits sparse callers (a handful of shots per media). The moment
 * filmstrip must pass something finer: its frame spacing is
 * `duration × 140 / ((zoom / 100) × containerWidth)`, which at maximum zoom is
 * ~0.017 s on a 10 s media — coarser bucketing there collapses neighbouring
 * frames into one image and the strip visibly repeats itself.
 */
export const DEFAULT_TIMESTAMP_QUANTUM = 0.1;

/**
 * How many times a consumer will ask for the same frame before giving up.
 *
 * Consumers read through the cache and treat a miss as "capture it again", which
 * is what lets an evicted frame come back. The cap is the backstop for the one
 * case where that would never settle: a frame captured and then immediately
 * evicted reads back as missing forever. Shared so the two consumers cannot
 * disagree about it.
 */
export const MAX_CAPTURE_ATTEMPTS = 3;

export type FrameCaptureOptions = {
  /** Cap on the captured JPEG's width. */
  maxWidth?: number;
  /** Cap on the captured JPEG's height. */
  maxHeight?: number;
  /** JPEG quality, 0–1. */
  quality?: number;
  /** Timestamp bucket size in seconds. See {@link DEFAULT_TIMESTAMP_QUANTUM}. */
  timestampQuantum?: number;
};

export const mediaKeyOf = (libraryId: string, mediaId: string) => `${libraryId}:${mediaId}`;

/**
 * The part of a frame's identity that describes its pixels rather than its
 * position — size and quality. Two requests differing only here are different
 * images at the same instant, so they must not share a cache entry.
 */
export const frameVariant = (options?: FrameCaptureOptions) =>
  `${options?.maxWidth ?? 0}x${options?.maxHeight ?? 0}:q${options?.quality ?? DEFAULT_CAPTURE_QUALITY}`;

/**
 * Snap a timestamp onto its bucket. Returns seconds, rounded to a fixed number
 * of decimals so `0.1 * 3` can't produce a different key than `0.30000000000000004`.
 */
export function quantiseTimestamp(
  timestamp: number,
  quantum: number = DEFAULT_TIMESTAMP_QUANTUM,
): number {
  const q = quantum > 0 ? quantum : DEFAULT_TIMESTAMP_QUANTUM;
  const safe = Number.isFinite(timestamp) ? Math.max(0, timestamp) : 0;
  const steps = Math.round(safe / q);
  return Number((steps * q).toFixed(6));
}

/**
 * Identity of a cached frame. Every input that changes the resulting pixels is
 * in here — size and quality included, because a 280px filmstrip frame and a
 * full-size thumbnail at the same instant are different images, and keying on
 * timestamp alone lets whichever renders first serve the other one the wrong size.
 */
export function frameCacheKey(
  libraryId: string,
  mediaId: string,
  timestamp: number,
  options?: FrameCaptureOptions,
): string {
  const t = quantiseTimestamp(timestamp, options?.timestampQuantum);
  return `${mediaKeyOf(libraryId, mediaId)}:${t}:${frameVariant(options)}`;
}

export type CaptureSizeInput = {
  sourceWidth: number;
  sourceHeight: number;
  maxWidth?: number;
  maxHeight?: number;
  /** Overridable only so tests don't have to reason about 1280. */
  maxDimension?: number;
};

/**
 * Fit a source frame inside whatever bounds the caller gave, preserving aspect
 * ratio and never scaling up.
 *
 * This fits inside the *box* — if only `maxWidth` is given, height follows the
 * ratio. That differs from the behaviour this replaced, which branched on
 * orientation and fitted portrait media to `maxHeight`, so a portrait clip
 * asked for `maxWidth: 280` came back up to 1280 tall.
 */
export function computeCaptureSize({
  sourceWidth,
  sourceHeight,
  maxWidth,
  maxHeight,
  maxDimension = MAX_CAPTURE_DIMENSION,
}: CaptureSizeInput): { width: number; height: number } {
  if (!sourceWidth || !sourceHeight || sourceWidth < 0 || sourceHeight < 0) {
    return { width: 0, height: 0 };
  }

  let scale = 1;
  if (maxWidth && maxWidth > 0) {
    scale = Math.min(scale, maxWidth / sourceWidth);
  }
  if (maxHeight && maxHeight > 0) {
    scale = Math.min(scale, maxHeight / sourceHeight);
  }
  if (maxDimension > 0) {
    // Capping the longest side after scaling is the same as folding the cap
    // into the scale factor up front.
    scale = Math.min(scale, maxDimension / Math.max(sourceWidth, sourceHeight));
  }

  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
}

export type PendingJob = {
  key: string;
  libraryId: string;
  mediaId: string;
  timestamp: number;
};

/**
 * Choose which pending job to run next: stay on the media the hidden player
 * already has attached and seek *forward* where possible, since a forward seek
 * within buffered segments costs a fraction of a re-attach.
 *
 * Falls back to any job on the attached media, then to whatever is oldest.
 * Switching media is the expensive case and is deliberately last.
 */
export function pickNextJob<T extends PendingJob>(
  jobs: Iterable<T>,
  attachedMediaKey: string | undefined,
  playhead: number,
): T | null {
  let sameMediaAhead: T | null = null;
  let sameMediaAny: T | null = null;
  let first: T | null = null;

  for (const job of jobs) {
    if (!first) first = job;
    if (mediaKeyOf(job.libraryId, job.mediaId) !== attachedMediaKey) continue;
    if (!sameMediaAny || job.timestamp < sameMediaAny.timestamp) {
      sameMediaAny = job;
    }
    // A hair of tolerance so a job at the current playhead counts as "ahead"
    // rather than forcing a backward seek.
    if (job.timestamp >= playhead - 0.05) {
      if (!sameMediaAhead || job.timestamp < sameMediaAhead.timestamp) {
        sameMediaAhead = job;
      }
    }
  }

  return sameMediaAhead ?? sameMediaAny ?? first;
}

/**
 * Byte budget for cached frames. Bounded by size rather than entry count
 * because callers store wildly different frames — a 280px filmstrip frame is
 * a few KB while a full-size thumbnail is hundreds, so "300 entries" means
 * anything between a couple of MB and far too much.
 */
export const FRAME_CACHE_MAX_BYTES = 24 * 1024 * 1024;

/**
 * A captured frame, held as an object URL over a Blob rather than a base64 data
 * URL. Base64 is about a third larger than the bytes it encodes, lives in the
 * JS heap as a string, and is re-decoded on every render that mounts it.
 *
 * `bytes` is the Blob's real size, which is what the cache budgets against —
 * the URL string itself is a few dozen characters and tells you nothing.
 * `mediaKey`, `variant` and `timestamp` are kept so callers can enumerate the
 * frames belonging to one media without parsing keys back apart (they contain
 * UUIDs, which is exactly what made the previous key-parsing fragile).
 */
export type CachedFrame = {
  url: string;
  bytes: number;
  mediaKey: string;
  variant: string;
  timestamp: number;
};

/**
 * Revoking on eviction is what keeps the Blobs from leaking, and it is only
 * safe because reads promote recency: a frame that is on screen is re-read on
 * every render, so it stays at the young end of the order and is the last
 * thing evicted. That only holds while this is the *single* cache — a second
 * cache holding URLs it doesn't own would hand out links to revoked Blobs.
 */
export const createFrameCache = (maxBytes: number = FRAME_CACHE_MAX_BYTES) =>
  new LRUCache<string, CachedFrame>({
    maxSize: maxBytes,
    sizeCalculation: (value) => value.bytes || 1,
    dispose: (value) => {
      URL.revokeObjectURL(value.url);
    },
  });

export type FrameCache = ReturnType<typeof createFrameCache>;
